import { mergeAttributes, Node } from '@tiptap/core';

export type CustomIconType = 'fa' | 'img';

export interface CustomIconAttributes {
  iconType: CustomIconType;
  name: string;
  color?: string;
  showBackground?: boolean;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customIcon: {
      setCustomIcon: (attrs: CustomIconAttributes) => ReturnType;
    };
  }
}

export const CustomIcon = Node.create({
  name: 'customIcon',
  group: 'inline',
  inline: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      iconType: {
        default: 'fa',
        parseHTML: (element: HTMLElement) => element.dataset['iconType'],
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-icon-type': attributes['iconType'] }),
      },
      name: {
        default: null,
        parseHTML: (element: HTMLElement) => element.dataset['name'],
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-name': attributes['name'] }),
      },
      color: {
        default: null,
        parseHTML: (element: HTMLElement) => element.dataset['color'],
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['color'] ? { 'data-color': attributes['color'] } : {},
      },
      showBackground: {
        default: false,
        parseHTML: (element: HTMLElement) => element.dataset['showBackground'] === 'true',
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes['showBackground'] ? { 'data-show-background': 'true' } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: `span[data-type="${this.name}"]` }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const attrs = node.attrs as Record<string, unknown>;
    const attributes = mergeAttributes(HTMLAttributes, { 'data-type': this.name });

    const baseStyle =
      'display: inline-flex; align-items: center; justify-content: center; vertical-align: middle; flex-shrink: 0;';

    if (attrs['iconType'] === 'img') {
      return [
        'span',
        { ...attributes, style: `${baseStyle} width: 1.5em; height: 1.5em;` },
        [
          'img',
          {
            src: attrs['name'],
            alt: attrs['name'],
            style: 'height: 1.15em; width: 1.15em; object-fit: contain;',
            draggable: 'false',
          },
        ],
      ];
    }

    // FontAwesome icon
    const color = attrs['color'] as string | undefined;
    const colorStyle = color && color !== 'currentColor' ? `color: ${color};` : '';
    const showBg = attrs['showBackground'] as boolean;

    let bgStyle = '';
    if (showBg) {
      if (color && color !== 'currentColor') {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        bgStyle = `background-color: rgba(${r}, ${g}, ${b}, 0.15); border-radius: 6px; width: 1.75em; height: 1.75em;`;
      } else {
        bgStyle = 'background-color: rgba(0, 0, 0, 0.06); border-radius: 6px; width: 1.75em; height: 1.75em;';
      }
    } else {
      bgStyle = 'width: 1.5em; height: 1.5em;';
    }

    return [
      'span',
      { ...attributes, style: `${colorStyle} ${bgStyle} ${baseStyle} font-size: 1em;` },
      ['i', { class: `fas fa-${String(attrs['name'])}` }],
    ];
  },

  addCommands() {
    return {
      setCustomIcon:
        (iconAttrs) =>
        ({ chain }) => {
          return chain().insertContent({ type: this.name, attrs: iconAttrs }).run();
        },
    };
  },
});
